import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import DashboardNavbar from "../../components/DashboardNavbar";
import Sidebar from "../../components/Sidebar";
import Content from "../../components/Content";
import { Button, Form, Popup, Grid, Loader, Dropdown, Checkbox } from "semantic-ui-react";
import EngagementDashboard from "./EngagementDashboard"; // Import the EngagementDashboard component
import ConversationEngagement from "./conversationEngagement"; // Import the ConversationEngagement component
import { getGroupsData, getGroupUsersData } from "../../apis/index"; // Import the API call
import { fetchEngagement, getAllChatHistoryCached } from "../../actions/dataConsoleAction"; // Import fetchEngagement action
import "../../styles/DashboardNavbar.css";
import "../../styles/Dashboard.css";
import "../../styles/EngagementDashboard.css";
import 'semantic-ui-css/semantic.min.css';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DropLoader  from '../../components/DropLoader';
import { height, width } from "@mui/system";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';
import { getLocalTimeZoneAbbr } from '../../utils/formatMessageTimestamp';

const Index = ({ pageID, title }) => {
  const dispatch = useDispatch();
  const [toggleBtn, setToggleBtn] = useState(false);
  const [navTitle, setnavTitle] = useState("Admin Console");
  const [datasets, setDatasets] = useState([]);
  const [selectedDataset, setSelectedDataset] = useState("");
  const [selectedDatasetId, setSelectedDatasetId] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('engagement'); // Default to engagement tab
  const [loading, setLoading] = useState(true);

  // Global group and user state
  const [globalGroups, setGlobalGroups] = useState([]);
  const [globalSelectedGroup, setGlobalSelectedGroup] = useState("");
  const [globalUsers, setGlobalUsers] = useState([]);
  const [globalSelectedUsers, setGlobalSelectedUsers] = useState([]); // Changed to array for multi-select
  const [globalGroupLoading, setGlobalGroupLoading] = useState(false);
  const [globalUserLoading, setGlobalUserLoading] = useState(false);
  const [globalAllUsers, setGlobalAllUsers] = useState([]); // Store all users for default state
  const [userSearchQuery, setUserSearchQuery] = useState(""); // Search query for user dropdown

  // Global engagement data state
  const [globalEngagementData, setGlobalEngagementData] = useState(null);
  const [globalEngagementLoading, setGlobalEngagementLoading] = useState(false);

  // Global date state - Initialize with current week (local time)
  const [globalStartDate, setGlobalStartDate] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const prevMonday = new Date(today);
    prevMonday.setDate(today.getDate() - ((day + 6) % 7));
    prevMonday.setHours(0, 0, 0, 0);
    return prevMonday;
  });

  const [globalEndDate, setGlobalEndDate] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const prevMonday = new Date(today);
    prevMonday.setDate(today.getDate() - ((day + 6) % 7));
    const nextSunday = new Date(prevMonday);
    nextSunday.setDate(prevMonday.getDate() + 6);
    nextSunday.setHours(23, 59, 59, 999);
    return nextSunday;
  });
  
  const [isGlobalCalendarOpen, setIsGlobalCalendarOpen] = useState(false);

  // Fetch datasets and users from chat history (single API call)
  const hasFetchedChatHistoryRef = useRef(false);
  useEffect(() => {
    if (hasFetchedChatHistoryRef.current) return;
    hasFetchedChatHistoryRef.current = true;

    const fetchChatHistoryData = async () => {
      try {
        setLoading(true);
        setGlobalUserLoading(true);

        // Use Redux-cached paginated chat history (fetches only once across dashboards)
        const chatHistoryData = await dispatch(getAllChatHistoryCached());

        // Extract unique datasets
        const datasetMap = new Map();
        chatHistoryData.forEach((item) => {
          if (!datasetMap.has(item.datasetId)) {
            datasetMap.set(item.datasetId, item.datasetName);
          }
        });

        const datasets = Array.from(datasetMap, ([datasetId, datasetName]) => ({
          datasetId,
          datasetName,
        }));
        setDatasets(datasets);

        // Extract unique users
        const usersList = [];
        chatHistoryData.forEach((record) => {
          if (record.createdUTC) {
            if (!usersList.some((user) => user.userId === record.userId)) {
              usersList.push({
                userId: record.userId,
                username: record.createdBy.username,
              });
            }
          }
        });

        setGlobalAllUsers(usersList);
        setGlobalUsers(usersList);
        // setGlobalSelectedUsers(usersList.map(user => user.userId));

        setLoading(false);
        setGlobalUserLoading(false);
      } catch (error) {
        console.error('Error fetching chat history data:', error);
        setLoading(false);
        setGlobalUserLoading(false);
      }
    };

    fetchChatHistoryData();
  }, []);

  useEffect(() => {
    setnavTitle(title);
  }, [title]);

  // Fetch groups on component mount
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setGlobalGroupLoading(true);
        const response = await getGroupsData();
        if (response?.data?.success && response.data.data) {
          setGlobalGroups(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching groups:", error);
      } finally {
        setGlobalGroupLoading(false);
      }
    };

    fetchGroups();
  }, []);

  // Helper function to aggregate multiple user responses into single response
  const aggregateUserResponses = (userResponses, startDate, endDate) => {
    if (!userResponses || userResponses.length === 0) return null;
    if (userResponses.length === 1) return userResponses[0];

    // Helper to convert time string to seconds
    const timeToSeconds = (timeString) => {
      if (!timeString) return 0;
      const parts = timeString.split(" ");
      let seconds = 0;
      for (let i = 0; i < parts.length; i += 2) {
        const value = parseInt(parts[i], 10);
        const unit = parts[i + 1];
        if (unit.includes("hr")) seconds += value * 3600;
        else if (unit.includes("min")) seconds += value * 60;
        else if (unit.includes("sec")) seconds += value;
      }
      return seconds;
    };

    // Helper to format seconds back to time string
    const secondsToTime = (totalSeconds) => {
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      return `${hours > 0 ? hours + " hr " : ""}${minutes} min ${seconds} sec`.trim();
    };

    // Aggregate totalEngagementTime - combine all users' date-wise data
    const dateWiseMap = {};
    userResponses.forEach(response => {
      response.totalEngagementTime?.dateWiseTotalEngagementTime?.forEach(dateData => {
        if (!dateWiseMap[dateData.date]) {
          dateWiseMap[dateData.date] = { date: dateData.date, users: [] };
        }
        dateWiseMap[dateData.date].users.push(...dateData.users);
      });
    });

    // Aggregate engagementByDay - sum up engagement time per day
    const engagementByDayMap = {};
    userResponses.forEach(response => {
      response.engagementByDay?.forEach(dayData => {
        if (!engagementByDayMap[dayData.date]) {
          engagementByDayMap[dayData.date] = { date: dayData.date, engagementTimeSeconds: 0 };
        }
        engagementByDayMap[dayData.date].engagementTimeSeconds += timeToSeconds(dayData.engagementTime);
      });
    });

    const engagementByDay = Object.values(engagementByDayMap).map(day => ({
      date: day.date,
      engagementTime: secondsToTime(day.engagementTimeSeconds)
    }));

    // Aggregate totalSessionDurationForRange (now a simple string value, not an array)
    let totalSessionDurationSeconds = 0;
    userResponses.forEach(response => {
      if (response.totalSessionDurationForRange) {
        totalSessionDurationSeconds += timeToSeconds(response.totalSessionDurationForRange);
      }
    });

    // Aggregate averageSessionDurationForRange (now a simple string value, not an array)
    let totalAvgSessionSeconds = 0;
    let userCount = 0;
    userResponses.forEach(response => {
      if (response.averageSessionDurationForRange) {
        totalAvgSessionSeconds += timeToSeconds(response.averageSessionDurationForRange);
        userCount++;
      }
    });

    const avgSessionDuration = userCount > 0 ? totalAvgSessionSeconds / userCount : 0;

    // Aggregate peakHours - find the hour with highest engagementCount per date
    const peakHoursMap = {};
    userResponses.forEach(response => {
      response.peakHours?.forEach(peakHour => {
        const key = peakHour.date;
        if (!peakHoursMap[key] || peakHour.engagementCount > peakHoursMap[key].engagementCount) {
          peakHoursMap[key] = peakHour;
        }
      });
    });

    // Aggregate mostUsedDatasets - sum counts per dataset
    const datasetsMap = {};
    userResponses.forEach(response => {
      response.mostUsedDatasets?.forEach(datasetGroup => {
        const dateRange = datasetGroup.dateRange;
        if (!datasetsMap[dateRange]) {
          datasetsMap[dateRange] = { dateRange: dateRange, datasets: {} };
        }

        datasetGroup.datasets?.forEach(dataset => {
          const datasetId = dataset.datasetId;
          if (!datasetsMap[dateRange].datasets[datasetId]) {
            datasetsMap[dateRange].datasets[datasetId] = {
              datasetId: datasetId,
              count: 0,
              totalDuration: 0,
              totalQuestions: 0
            };
          }
          datasetsMap[dateRange].datasets[datasetId].count += dataset.count || 0;
          datasetsMap[dateRange].datasets[datasetId].totalDuration += parseFloat(dataset.totalDuration) || 0;
          datasetsMap[dateRange].datasets[datasetId].totalQuestions += dataset.totalQuestions || 0;
        });
      });
    });

    const mostUsedDatasets = Object.values(datasetsMap).map(item => ({
      dateRange: item.dateRange,
      datasets: Object.values(item.datasets)
    }));

    // Return aggregated response
    return {
      ...userResponses[0], // Use first response as template
      totalEngagementTime: {
        dateWiseTotalEngagementTime: Object.values(dateWiseMap)
      },
      engagementByDay: engagementByDay,
      totalSessionDurationForRange: secondsToTime(totalSessionDurationSeconds),
      averageSessionDurationForRange: secondsToTime(avgSessionDuration),
      peakHours: Object.values(peakHoursMap),
      mostUsedDatasets: mostUsedDatasets,
      longestConversations: userResponses.flatMap(r => r.longestConversations || []),
      frequentlyAskedQuestions: userResponses.flatMap(r => r.frequentlyAskedQuestions || [])
    };
  };

  // Handle group selection - fetch users in that group
  useEffect(() => {
    // Only fetch if a specific group is selected (not empty string)
    if (!globalSelectedGroup) {
      return;
    }

    // Start loading immediately and clear old data
    setGlobalEngagementLoading(true);
    setGlobalEngagementData(null);

    const fetchGroupUsers = async () => {
      try {
        setGlobalUserLoading(true);
        const response = await getGroupUsersData(globalSelectedGroup);
        if (response?.data?.success && response.data.data) {
          const groupUsers = response.data.data.map(member => ({
            userId: member.userId,
            username: member.username || member.userDisplayName || member.userEmail || member.userId,
          }));
          setGlobalUsers(groupUsers);
          // Auto-select all users in the group
          setGlobalSelectedUsers(groupUsers.map(user => user.userId));
        }
      } catch (error) {
        console.error("Error fetching group users:", error);
        setGlobalUsers([]);
        setGlobalSelectedUsers([]);
        setGlobalEngagementLoading(false);
      } finally {
        setGlobalUserLoading(false);
      }
    };

    fetchGroupUsers();
  }, [globalSelectedGroup]);

  // Global API call for engagement data - fetch once when filters change
  useEffect(() => {
    const fetchGlobalEngagementData = async () => {
      // Only fetch if we have valid dates (dataset is optional - null means all datasets)
      if (!globalStartDate || !globalEndDate) {
        setGlobalEngagementData(null);
        return;
      }

      try {
        // Clear old data first to prevent showing stale data
        setGlobalEngagementData(null);
        setGlobalEngagementLoading(true);

        // Determine strategy based on selection:
        // 1. No users selected OR all users selected (no group) -> single API call with userId = null
        // 2. Single user selected -> single API call with that userId
        // 3. Group selected (multiple users) -> multiple API calls, one per user, then aggregate

        if (globalSelectedUsers.length === 0 && !globalSelectedGroup) {
          // No users selected - fetch for all users with userId = null
          // Use selectedDatasetId or null if no dataset selected (for all datasets)
          const response = await dispatch(
            fetchEngagement(
              null,
              selectedDatasetId || null,
              globalStartDate,
              globalEndDate
            )
          );
          setGlobalEngagementData(response);
        } else if (globalSelectedUsers.length === 1) {
          // Single user selected
          const response = await dispatch(
            fetchEngagement(
              globalSelectedUsers[0],
              selectedDatasetId || null,
              globalStartDate,
              globalEndDate
            )
          );
          setGlobalEngagementData(response);
        } else if (globalSelectedUsers.length > 1) {
          // Group selected (multiple specific users) - make multiple calls and aggregate
          const userResponses = await Promise.all(
            globalSelectedUsers.map(userId =>
              dispatch(
                fetchEngagement(
                  userId,
                  selectedDatasetId || null,
                  globalStartDate,
                  globalEndDate
                )
              )
            )
          );

          // Aggregate all responses
          const aggregatedData = aggregateUserResponses(userResponses, globalStartDate, globalEndDate);
          setGlobalEngagementData(aggregatedData);
        } else {
          // Shouldn't reach here, but handle it
          setGlobalEngagementData(null);
        }
      } catch (error) {
        console.error("Error fetching global engagement data:", error);
        setGlobalEngagementData(null);
      } finally {
        setGlobalEngagementLoading(false);
      }
    };

    fetchGlobalEngagementData();
  }, [globalStartDate, globalEndDate, selectedDatasetId, globalSelectedUsers, dispatch]);

  const toggle = () => setToggleBtn((val) => !val);

  const handleCloseButtonClick = () => {
    setToggleBtn(false); // Set toggleBtn to false
  };

  const handleDatasetChange = (event, { value }) => {
    if (value === "") {
      // "All Datasets" selected - reset dataset selection
      setSelectedDataset("");
      setSelectedDatasetId("");
      setIsOpen(false);
    } else {
      const selected = datasets.find(dataset => dataset.datasetName === value); // Find dataset by name
      if (selected) {
        setSelectedDataset(selected.datasetName); // Set the selected dataset name
        setSelectedDatasetId(String(selected.datasetId)); // Set the selected dataset ID
        setIsOpen(false); // Close the dropdown after selection
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    // Since we're storing UTC dates, extract UTC components to display the correct date
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();

    // Create a local date with the UTC components for formatting
    const displayDate = new Date(year, month, day);

    return displayDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Global Date Range Filter Component
  const GlobalDateRangeFilter = () => {
    const handleDateChange = (dates) => {
      const [start, end] = dates;

      // Normalize dates to local start/end of day
      if (start && end && start.getTime() === end.getTime()) {
        const startOfDay = new Date(start);
        startOfDay.setHours(0, 0, 0, 0);
        const endOfDay = new Date(start);
        endOfDay.setHours(23, 59, 59, 999);

        setGlobalStartDate(startOfDay);
        setGlobalEndDate(endOfDay);
      } else {
        if (start) {
          const startOfDay = new Date(start);
          startOfDay.setHours(0, 0, 0, 0);
          setGlobalStartDate(startOfDay);
        } else {
          setGlobalStartDate(start);
        }

        if (end) {
          const endOfDay = new Date(end);
          endOfDay.setHours(23, 59, 59, 999);
          setGlobalEndDate(endOfDay);
        } else {
          setGlobalEndDate(end);
        }
      }

      // Close calendar when both dates are selected
      if (start && end) {
        setIsGlobalCalendarOpen(false);
      }
    };

    // Helper to get local date for DatePicker display
    const toLocalDateForPicker = (date, isEndDate = false) => {
      if (!date) return null;
      const localDate = new Date(date);
      localDate.setHours(0, 0, 0, 0);
      return localDate;
    };

    return (
      <div className="global-date-filter" style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div className="date-picker-containers">
          <div className="calendar-icon" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
            <FontAwesomeIcon
              icon={faCalendar}
              onClick={() => setIsGlobalCalendarOpen((prev) => !prev)}
              style={{ marginRight: '0px' }}
            />
            <DatePicker
              selected={toLocalDateForPicker(globalStartDate)}
              onChange={handleDateChange}
              startDate={toLocalDateForPicker(globalStartDate)}
              endDate={toLocalDateForPicker(globalEndDate)}
              selectsRange
              dateFormat="yyyy/MM/dd"
              className="date-picker"
              popperPlacement="bottom-end"
              popperModifiers={{
                preventOverflow: {
                  enabled: true,
                  boundariesElement: 'viewport',
                },
              }}
              popperClassName="custom-datepicker-popper"
              customInput={<input style={{ display: 'none' }} />}
              open={isGlobalCalendarOpen}
              onClickOutside={() => setIsGlobalCalendarOpen(false)}
              onCalendarClose={() => setIsGlobalCalendarOpen(false)}
              isClearable={false}
              placeholderText="Select date range"
            />
          </div>
          <span>{formatDate(globalStartDate)}</span>
          <span className="date-separator" style={{ margin: '0 4px' }}>-</span>
          <span>{globalEndDate ? formatDate(globalEndDate) : 'Select end date'}</span>
          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#888', fontWeight: '500' }}>
            ({getLocalTimeZoneAbbr(new Date())})
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="top-wrapper">
      <Sidebar toggleBtn={toggleBtn} onCloseButtonClick={handleCloseButtonClick} activeMenu="Dashboards"/>
      <DashboardNavbar setToggle={toggle} navTitle={navTitle} type={pageID} />
      <Content toggleBtn={toggleBtn}>
        <Grid stackable>
          <Grid.Row className="engagement-controls-row" style={{ position: 'relative', paddingBottom: '20px', paddingTop: '15px', paddingLeft: '20px', paddingRight: '20px' }}>
            <div className="controls-wrapper" style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              flexWrap: 'wrap',
              gap: '15px',
              justifyContent: 'space-between'
            }}>
              {/* Dataset dropdown */}
              <div className="dataset-control" style={{ minWidth: '11em' }}>
                <Form.Select
                  className="dataset-select"
                  value={selectedDataset}
                  onChange={handleDatasetChange}
                  options={[
                    { key: "all", text: "All Datasets", value: "" },
                    ...datasets.map((dataset) => ({
                      key: dataset.datasetId,
                      text: dataset.datasetName,
                      value: dataset.datasetName,
                    }))
                  ]}
                  placeholder="Select Dataset"
                  search
                  selectOnBlur={false}
                  style={{ borderRadius:"6px",minWidth:"11em",height:"40px"}}
                  icon={
                    loading ? (
                      <DropLoader className="custom-dropdown-icon" style={{ fontSize: "x-small",width:"40px",height:"40px"}} />
                    ) : (
                      <KeyboardArrowDownIcon className="custom-dropdown-icon" style={{ fontSize: "large" }} />
                    )
                  }
                />
              </div>

              {/* Tabs for Engagement Time and Conversation Time */}
              <div className="tab--container tab-control" style={{ minWidth: 'fit-content' }}>
                <div className={`tabs ${activeTab === 'engagement' ? 'active' : ''}`} onClick={() => setActiveTab('engagement')}>
                  Engagement Time
                </div>
                <div className={`tabs ${activeTab === 'conversation' ? 'active' : ''}`} onClick={() => setActiveTab('conversation')}>
                  Conversation Time
                </div>
              </div>

              {/* Spacer to push right controls to the end on desktop */}
              <div className="spacer-control" style={{ flex: '1 1 auto', minWidth: '20px' }}></div>

              {/* Right controls wrapper with margin to align with graph */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap', marginRight: '40px' }}>
                {/* Group dropdown */}
                <div className="group-control" style={{ minWidth: '11em' }}>
                  <Form.Select
                    placeholder="Select Group"
                    value={globalSelectedGroup}
                    onChange={(e, { value }) => {
                      setGlobalSelectedGroup(value);
                      // If "All Users" selected, reset to all users and clear selection
                      if (value === "") {
                        setGlobalUsers(globalAllUsers);
                        setGlobalSelectedUsers([]);
                      }
                    }}
                    options={[
                      { key: "all", text: "All Users", value: "" },
                      ...globalGroups.map((group) => ({
                        key: group.id,
                        text: group.name,
                        value: group.id,
                      }))
                    ]}
                    search
                    style={{
                      borderRadius: "6px",
                      minWidth: "11em",
                      height: "40px",
                    }}
                    icon={
                      globalGroupLoading ? (
                        <DropLoader
                          className="custom-dropdown-icon"
                          style={{
                            fontSize: "x-small",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          className="custom-dropdown-icon"
                          style={{ fontSize: "large" }}
                        />
                      )
                    }
                  />
                </div>

                {/* User dropdown - Multi-select with checkboxes */}
                <div className="user-control" style={{ minWidth: '11em' }}>
                  <Dropdown
                    placeholder={
                      globalSelectedUsers.length === 0
                        ? "Select User"
                        : globalSelectedUsers.length === globalUsers.length
                        ? "All Users"
                        : `${globalSelectedUsers.length} user${globalSelectedUsers.length > 1 ? 's' : ''} selected`
                    }
                    fluid
                    selection
                    search
                    scrolling
                    disabled={globalUsers.length === 0}
                    searchQuery={userSearchQuery}
                    onSearchChange={(e, { searchQuery }) => setUserSearchQuery(searchQuery)}
                    icon={
                      globalUserLoading ? (
                        <DropLoader
                          className="custom-dropdown-icon"
                          style={{
                            fontSize: "x-small",
                            width: "40px",
                            height: "40px",
                          }}
                        />
                      ) : (
                        <KeyboardArrowDownIcon
                          className="custom-dropdown-icon"
                          style={{ fontSize: "large" }}
                        />
                      )
                    }
                    style={{
                      borderRadius: "6px",
                      minWidth: "11em",
                      height: "40px",
                    }}
                  >
                    <Dropdown.Menu>
                      {/* Show "All Users" option only if search is empty OR "all users" matches the search */}
                      {(!userSearchQuery || "all users".includes(userSearchQuery.toLowerCase())) && (
                        <>
                          <Dropdown.Item
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalSelectedUsers.length === globalUsers.length) {
                                setGlobalSelectedUsers([]);
                              } else {
                                setGlobalSelectedUsers(globalUsers.map(user => user.userId));
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                              <span>{globalSelectedGroup ? "All Users in Group" : "All Users"}</span>
                              <input
                                type="checkbox"
                                checked={globalSelectedUsers.length === globalUsers.length}
                                onChange={() => {
                                  if (globalSelectedUsers.length === globalUsers.length) {
                                    setGlobalSelectedUsers([]);
                                  } else {
                                    setGlobalSelectedUsers(globalUsers.map(user => user.userId));
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                          </Dropdown.Item>
                          <Dropdown.Divider />
                        </>
                      )}
                      {/* Filter users based on search query */}
                      {globalUsers
                        .filter((user) =>
                          !userSearchQuery ||
                          user.username.toLowerCase().includes(userSearchQuery.toLowerCase())
                        )
                        .map((user) => (
                          <Dropdown.Item
                            key={user.userId}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (globalSelectedUsers.includes(user.userId)) {
                                setGlobalSelectedUsers(globalSelectedUsers.filter(id => id !== user.userId));
                              } else {
                                setGlobalSelectedUsers([...globalSelectedUsers, user.userId]);
                              }
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
                              <span>{user.username}</span>
                              <input
                                type="checkbox"
                                checked={globalSelectedUsers.includes(user.userId)}
                                onChange={() => {
                                  if (globalSelectedUsers.includes(user.userId)) {
                                    setGlobalSelectedUsers(globalSelectedUsers.filter(id => id !== user.userId));
                                  } else {
                                    setGlobalSelectedUsers([...globalSelectedUsers, user.userId]);
                                  }
                                }}
                                style={{ cursor: 'pointer' }}
                              />
                            </div>
                          </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </div>

                {/* Global Date Range Filter */}
                <div className="calendar-control" style={{ minWidth: 'fit-content' }}>
                  <GlobalDateRangeFilter />
                </div>
              </div>
            </div>
          </Grid.Row>
        </Grid>

        {/* Render the appropriate dashboard based on the active tab */}
        {activeTab === 'engagement' && (
          <EngagementDashboard
            selectedDatasetId={selectedDatasetId}
            selectedDatasetName={selectedDataset}
            globalStartDate={globalStartDate}
            globalEndDate={globalEndDate}
            globalSelectedGroup={globalSelectedGroup}
            globalSelectedUsers={globalSelectedUsers}
            globalUsers={globalUsers}
            globalEngagementData={globalEngagementData}
            globalEngagementLoading={globalEngagementLoading}
            datasets={datasets}
            globalGroups={globalGroups}
          />
        )}
        {activeTab === 'conversation' && (
          <ConversationEngagement
            selectedDatasetId={selectedDatasetId}
            selectedDatasetName={selectedDataset}
            globalStartDate={globalStartDate}
            globalEndDate={globalEndDate}
            globalSelectedGroup={globalSelectedGroup}
            globalSelectedUsers={globalSelectedUsers}
            globalUsers={globalUsers}
            globalEngagementData={globalEngagementData}
            globalEngagementLoading={globalEngagementLoading}
            globalGroups={globalGroups}
          />
        )}
      </Content>
    </div>
  );
};

export default Index;